import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import shopingCardData from '../data/shopingCardData.json';
import { useCart } from '../../context/CartContext.jsx';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useWishlist } from '../../context/WishlistContext.jsx';
import { FaHeart, FaRegHeart, FaArrowLeft } from 'react-icons/fa';
import './ProductDetails.scss';

const ProductDetails = () => {
    const { addToCart } = useCart();
    const { toggleWishlist, isInWishlist } = useWishlist();
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [selectedSize, setSelectedSize] = useState('M');
    const [activeImage, setActiveImage] = useState('');

    useEffect(() => {
        const foundProduct = shopingCardData.shopingCard.find(item => item.id === parseInt(id));
        if (foundProduct) {
            setProduct(foundProduct);
            setActiveImage(foundProduct.productImage);
            // Get related products (same category, excluding current)
            const related = shopingCardData.shopingCard
                .filter(item => item.category === foundProduct.category && item.id !== foundProduct.id)
                .slice(0, 4);
            setRelatedProducts(related);
        } else {
            // If product not found, redirect to home or show error
            navigate('/');
        }
    }, [id, navigate]);

    if (!product) return <div className="loading-spinner">Loading...</div>;

    const sizes = ['S', 'M', 'L', 'XL', 'XXL'];

    const handleAddToCart = () => {
        addToCart(product, selectedSize);
        // Optional: show a toast or feedback
        alert(`${product.title} (Size: ${selectedSize}) added to cart!`);
    };

    const handleBuyNow = () => {
        addToCart(product, selectedSize);
        navigate('/checkout');
    };

    return (
        <div className="product-details-page padding_t88">
            <div className="back-button-section py-3">
                <Container>
                    <button className="back-btn d-flex align-items-center gap-2" onClick={() => navigate(-1)}>
                        <FaArrowLeft /> <span>Back</span>
                    </button>
                </Container>
            </div>

            <Container className="product-view-container my-4">
                <Row>
                    <Col lg={7} className="product-gallery-section" data-aos="fade-right">
                        <Row className="flex-column-reverse flex-md-row">
                            <Col md={2} className="thumbnail-column mt-3 mt-md-0 d-flex flex-row flex-md-column gap-2 overflow-auto">
                                {[product.productImage, ...(product.images || [])].filter((v, i, a) => a.indexOf(v) === i).map((img, idx) => (
                                    <div 
                                        key={idx} 
                                        className={`thumb-wrapper ${activeImage === img ? 'active' : ''}`}
                                        onClick={() => setActiveImage(img)}
                                    >
                                        <img src={img} alt={`${product.title}-${idx}`} className="img-fluid" />
                                    </div>
                                ))}
                            </Col>
                            <Col md={10} className="main-image-column">
                                <div className="main-image-wrapper glassmorphism">
                                    <img src={activeImage} alt={product.title} className="img-fluid main-product-image" />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col lg={5} className="product-info-section" data-aos="fade-left">
                        <div className="info-card p-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h4 className="category-label text-uppercase m-0">{product.category}</h4>
                                <div className="rating-badge">
                                    <i className="fas fa-star text-warning"></i> 4.8
                                </div>
                            </div>
                            <h1 className="product-title fw-bold mb-3">{product.title}</h1>
                            <div className="price-tag mb-4">
                                <span className="currency">$</span>
                                <span className="amount">{product.price}</span>
                                <span className="old-price ms-3">${(product.price * 1.5).toFixed(2)}</span>
                            </div>

                            <p className="short-desc mb-4">
                                {product.shortInfo}
                            </p>

                            <div className="size-selector mb-4">
                                <p className="fw-bold mb-2">CHOOSE YOUR SIZE</p>
                                <div className="d-flex gap-2">
                                    {sizes.map(size => (
                                        <div
                                            key={size}
                                            className={`size-option ${selectedSize === size ? 'active' : ''}`}
                                            onClick={() => setSelectedSize(size)}
                                        >
                                            {size}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="action-buttons d-flex gap-3 mb-4">
                                <Button className="dBtn btnPrimary flex-grow-1" onClick={handleAddToCart}>ADD TO CART</Button>
                                <Button className="dBtn btnBorder flex-grow-1" onClick={handleBuyNow}>BUY NOW</Button>
                                <Button 
                                    className={`wishlist-toggle-btn ${isInWishlist(product.id) ? 'active' : ''}`}
                                    onClick={() => toggleWishlist(product)}
                                    title={isInWishlist(product.id) ? "Remove from Wishlist" : "Add to Wishlist"}
                                >
                                    {isInWishlist(product.id) ? <FaHeart size={20} /> : <FaRegHeart size={20} />}
                                </Button>
                            </div>

                            <hr className="my-4" />

                            <div className="specs-section">
                                <h5 className="fw-bold mb-3">Product Specifications</h5>
                                <div className="specs-grid">
                                    <div className="spec-item">
                                        <span className="label">Fabric:</span>
                                        <span className="value">{product.specs?.fabric || "Premium Cotton"}</span>
                                    </div>
                                    <div className="spec-item">
                                        <span className="label">Fit:</span>
                                        <span className="value">{product.specs?.fit || "Slim Fit"}</span>
                                    </div>
                                    <div className="spec-item">
                                        <span className="label">Care:</span>
                                        <span className="value">{product.specs?.care || "Machine Wash"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            <section className="related-products-section sectionPadding">
                <Container>
                    <div className="section-header text-center mb-5">
                        <h4 className="section-title-sm">You Might Also Like</h4>
                        <h2 className="title-md fw-bold">RELATED PRODUCTS</h2>
                    </div>
                    <Row className="g-4">
                        {relatedProducts.map((item) => (
                            <Col key={item.id} lg={3} md={4} sm={6} xs={6}>
                                <div
                                    className="shop_card pointer-cursor position-relative"
                                    onClick={() => navigate(`/product/${item.id}`)}
                                >
                                    <button 
                                        className={`wishlist_btn ${isInWishlist(item.id) ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleWishlist(item);
                                        }}
                                    >
                                        {isInWishlist(item.id) ? <FaHeart /> : <FaRegHeart />}
                                    </button>
                                    <div className="img-holder">
                                        <img className="card_image" src={item.productImage} alt={item.title} />
                                    </div>
                                    <div className="text-center content_layer p-2">
                                        <p className="category_name">{item.category}</p>
                                        <p className="card_title">{item.title}</p>
                                        <p className="price">${item.price}</p>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default ProductDetails;
